import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
  Card,
} from '@mui/material';
import { useTheme } from '../../contexts/ThemeContext';
import { ToggleButtonGroup, ToggleButton } from '@mui/material';
import { Upload, LocationOn, AttachMoney } from '@mui/icons-material';
import { parsePriceInput, formatCentavosBR, centavosToNumber } from '../../utils/priceParser';
import { useDialogKeyboardScroll } from '../../hooks/useDialogKeyboardScroll';

interface PreviewData {
  title: string;
  image: string;
  screenshot?: string;
  price?: string;
  description?: string;
  domain?: string;
}

interface AddWishlistItemDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: {
    title: string;
    url?: string;
    imageUrl: string;
    price?: number;
    store?: string;
    imageFile?: File;
  }) => Promise<void>;
}

export const AddWishlistItemDialog: React.FC<AddWishlistItemDialogProps> = ({
  open,
  onClose,
  onAdd,
}) => {
  const { theme } = useTheme();
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [store, setStore] = useState('');
  const [priceCents, setPriceCents] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'url' | 'upload' | 'text'>('url');
  useDialogKeyboardScroll(open);

  const handleModeChange = (newMode: 'url' | 'upload' | 'text') => {
    setMode(newMode);
    setUrl('');
    setTitle('');
    setStore('');
    setPriceCents('');
    setImageFile(null);
    setPreview(null);
    setError(null);
  };

  const fetchPreview = async (url: string) => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = import.meta.env.VITE_SERVICE_URL_HOSTLY_API;
      const apiKey = import.meta.env.VITE_API_SECRET_KEY;

      const response = await fetch(`${apiUrl}/preview?url=${encodeURIComponent(url)}`, {
        method: 'GET',
        headers: {
          'X-API-Key': apiKey,
          'Content-Type': 'application/json',
        },
      });

      const urlObj = new URL(url);
      const domain = urlObj.hostname.replace('www.', '');

      if (response.status === 403 || response.status === 502 || response.status === 504) {
        const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
        setPreview({
          title: domain,
          image: faviconUrl,
          domain: domain,
        });
        setError(`Ops, não conseguimos carregar os dados do ${domain}`);
        return;
      }

      if (!response.ok) throw new Error('Erro ao carregar preview');

      const data = await response.json();

      setPreview({
        title: data.title || domain,
        image: data.image || `https://www.google.com/s2/favicons?domain=${domain}&sz=128`,
        screenshot: data.screenshot,
        description: data.description || '',
        domain: domain,
        price: data.price,
      });

      if (!store.trim()) {
        const storeName = domain.split('.')[0];
        setStore(storeName.charAt(0).toUpperCase() + storeName.slice(1));
      }

      if (data.price && !priceCents) {
        const num = parseFloat(String(data.price).replace(',', '.'));
        if (Number.isFinite(num)) {
          setPriceCents(String(Math.round(num * 100)));
        }
      }
    } catch (err) {
      console.error('Erro ao buscar preview:', err);
      try {
        const urlObj = new URL(url);
        const domain = urlObj.hostname.replace('www.', '');
        const faviconUrl = `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
        setPreview({
          title: domain,
          image: faviconUrl,
          domain: domain,
        });
        setError(`Ops, não conseguimos carregar os dados do ${domain}`);
      } catch {
        setError('URL inválida');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === 'text') {
      setPreview(null);
      setError(null);
      return;
    }
    if (url.trim() && mode === 'url') {
      // Validar se é uma URL válida
      try {
        new URL(url);
        const timer = setTimeout(() => {
          fetchPreview(url);
        }, 800);

        return () => clearTimeout(timer);
      } catch {
        setError('URL inválida');
        setPreview(null);
      }
    } else {
      setPreview(null);
      setError(null);
    }
  }, [url, mode]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 15 * 1024 * 1024) {
        setError('Imagem deve ter no máximo 15MB');
        return;
      }
      setImageFile(file);
      setPreview({
        title: title || file.name,
        image: URL.createObjectURL(file),
      });
    }
  };

  const handleSubmit = async () => {
    if (mode === 'url' && !url.trim()) return;
    if (mode === 'upload' && (!imageFile || !title.trim())) return;
    if (mode === 'text' && !title.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const data: any = {
        title:
          mode === 'upload' || mode === 'text'
            ? title.trim()
            : (preview?.title || url),
        imageUrl: mode === 'url' && !imageFile ? (preview?.image || '') : '',
      };

      if (url.trim()) {
        data.url = url.trim();
      }

      const parsedCents = centavosToNumber(priceCents);
      if (parsedCents !== null) {
        data.price = parsedCents;
      } else if (preview?.price) {
        const parsed = parsePriceInput(preview.price);
        if (parsed !== null) data.price = parsed;
      }

      if (store.trim()) {
        data.store = store.trim();
      }

      if (mode === 'upload' && imageFile) {
        data.imageFile = imageFile;
      }

      await onAdd(data);

      setUrl('');
      setTitle('');
      setStore('');
      setPriceCents('');
      setImageFile(null);
      setPreview(null);
      setError(null);

      onClose();
    } catch (err) {
      console.error('Erro no submit:', err);
      setError('Erro ao adicionar item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: 'calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 32px)',
          marginTop: 'env(safe-area-inset-top)',
          marginBottom: 'env(safe-area-inset-bottom)',
        },
      }}
    >
      <DialogTitle>Adicionar item à wishlist</DialogTitle>
      <DialogContent sx={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Box sx={{ mb: 2 }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_, newMode) => newMode && handleModeChange(newMode)}
          >
            <ToggleButton value="url">Link</ToggleButton>
            <ToggleButton value="upload">Upload</ToggleButton>
            <ToggleButton value="text">Lista</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {mode === 'text' ? (
          <>
            <TextField
              fullWidth
              label="Nome do item"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              margin="normal"
              autoFocus
              placeholder="Ex: Xícaras brancas, Toalha de mesa..."
              helperText="Adicione apenas o nome do item, sem foto."
            />
            <TextField
              fullWidth
              label="Link (opcional)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              margin="normal"
              placeholder="https://..."
              slotProps={{ htmlInput: { autoCapitalize: 'none', autoCorrect: 'off' } }}
            />
            <TextField
              fullWidth
              label="Nome da loja (opcional)"
              value={store}
              onChange={(e) => setStore(e.target.value)}
              margin="normal"
              placeholder="Ex: Amazon, Camicado, etc."
            />
            <TextField
              fullWidth
              label="Valor (opcional)"
              value={formatCentavosBR(priceCents)}
              onChange={(e) => setPriceCents(e.target.value.replace(/\D/g, ''))}
              margin="normal"
              placeholder="0,00"
              slotProps={{
                input: {
                  startAdornment: <Box component="span" sx={{ mr: 0.5 }}>R$</Box>,
                  inputMode: 'numeric',
                },
                htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
              }}
            />
          </>
        ) : mode === 'url' ? (
          <>
            <TextField
              fullWidth
              label="URL do produto"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              margin="normal"
              placeholder="Cole o link do produto aqui"
              error={!!error}
              helperText={error}
            />

            <Button
              component="label"
              variant="outlined"
              startIcon={<Upload />}
              sx={{ mt: 1, mb: 1 }}
              fullWidth
            >
              {imageFile ? `Imagem: ${imageFile.name}` : 'Substituir imagem (opcional)'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>

            <TextField
              fullWidth
              label="Nome da loja (opcional)"
              value={store}
              onChange={(e) => setStore(e.target.value)}
              margin="normal"
              placeholder="Ex: Amazon, Camicado, etc."
            />
            <TextField
              fullWidth
              label="Valor (opcional)"
              value={formatCentavosBR(priceCents)}
              onChange={(e) => setPriceCents(e.target.value.replace(/\D/g, ''))}
              margin="normal"
              placeholder="0,00"
              slotProps={{
                input: {
                  startAdornment: <Box component="span" sx={{ mr: 0.5 }}>R$</Box>,
                  inputMode: 'numeric',
                },
                htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
              }}
            />
          </>
        ) : (
          <>
            <TextField
              fullWidth
              label="Nome do item"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Link (opcional)"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              margin="normal"
              placeholder="https://..."
              slotProps={{ htmlInput: { autoCapitalize: 'none', autoCorrect: 'off' } }}
            />
            <TextField
              fullWidth
              label="Nome da loja (opcional)"
              value={store}
              onChange={(e) => setStore(e.target.value)}
              margin="normal"
              placeholder="Ex: Amazon, Camicado, etc."
            />
            <TextField
              fullWidth
              label="Valor (opcional)"
              value={formatCentavosBR(priceCents)}
              onChange={(e) => setPriceCents(e.target.value.replace(/\D/g, ''))}
              margin="normal"
              placeholder="0,00"
              slotProps={{
                input: {
                  startAdornment: <Box component="span" sx={{ mr: 0.5 }}>R$</Box>,
                  inputMode: 'numeric',
                },
                htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
              }}
            />
            <Button
              component="label"
              variant="outlined"
              startIcon={<Upload />}
              sx={{ mt: 1 }}
            >
              Escolher Imagem
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleImageUpload}
              />
            </Button>
          </>
        )}

        {loading && (
          <Box display="flex" flexDirection="column" alignItems="center" my={2}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              Buscando informações...
            </Typography>
          </Box>
        )}

        {preview && !loading && (
          <Card sx={{ mt: 2, border: `1px solid ${theme.colors.border}` }}>
            {preview.image && (
              <Box
                sx={{
                  height: 200,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: preview.image.includes('favicon') ? theme.colors.background : '#f5f5f5',
                }}
              >
                <img
                  src={preview.image}
                  alt={preview.title}
                  style={{
                    maxWidth: preview.image.includes('favicon') ? '64px' : '100%',
                    maxHeight: preview.image.includes('favicon') ? '64px' : '200px',
                    objectFit: 'contain',
                  }}
                />
              </Box>
            )}
            <Box p={2}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                {preview.title}
              </Typography>
              {preview.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {preview.description}
                </Typography>
              )}
              {preview.domain && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOn sx={{ fontSize: '0.8rem' }} />
                  {preview.domain}
                </Typography>
              )}
              {priceCents && (
                <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AttachMoney sx={{ fontSize: '1rem' }} />
                  R$ {formatCentavosBR(priceCents)}
                </Typography>
              )}
            </Box>
          </Card>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={
            loading ||
            (mode === 'url' && !url.trim()) ||
            (mode === 'upload' && (!imageFile || !title.trim())) ||
            (mode === 'text' && !title.trim())
          }
          variant="contained"
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: 'white' }} />
              Adicionando...
            </Box>
          ) : (
            'Adicionar'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
